import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookOpen, Save, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function AdminErlaeuterung() {
  const [form, setForm] = useState({
    sample_analysis_id: '',
    hero_title: '',
    hero_subtitle: '',
    about_tool: '',
    how_it_works: '',
    about_company: '',
    industry_value: '',
    features_title: '',
    cta_text: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch(`${API_URL}/api/admin/erlaeuterung`, { headers })
      .then(r => r.json())
      .then(d => {
        setForm(f => ({
          ...f,
          sample_analysis_id: d.sample_analysis_id || '',
          hero_title: d.hero_title || '',
          hero_subtitle: d.hero_subtitle || '',
          about_tool: d.about_tool || '',
          how_it_works: d.how_it_works || '',
          about_company: d.about_company || '',
          industry_value: d.industry_value || '',
          features_title: d.features_title || '',
          cta_text: d.cta_text || '',
        }));
      })
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/admin/erlaeuterung`, {
        method: 'PUT', headers, body: JSON.stringify(form),
      });
      toast.success('Erläuterungsseite gespeichert');
    } catch {
      toast.error('Fehler beim Speichern');
    }
    setSaving(false);
  };

  const update = (key, value) => setForm(f => ({ ...f, [key]: value }));

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-erlaeuterung">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Erläuterungsseite</h1>
          <p className="text-sm text-muted-foreground">Inhalte der öffentlichen Info-Seite verwalten</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/erlaeuterung" target="_blank" rel="noopener">
            <Button variant="outline" size="sm" className="gap-1.5 text-xs">
              <ExternalLink className="h-3.5 w-3.5" /> Vorschau
            </Button>
          </a>
          <Button onClick={handleSave} disabled={saving} className="gap-1.5" data-testid="save-erlaeuterung">
            <Save className="h-3.5 w-3.5" /> {saving ? 'Speichern...' : 'Speichern'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Musteranalyse</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Analyse-ID der Musteranalyse</Label>
            <Input value={form.sample_analysis_id} onChange={e => update('sample_analysis_id', e.target.value)} placeholder="z.B. A-2026-XXXX" data-testid="sample-analysis-id" />
            <p className="text-[11px] text-muted-foreground mt-1">Die Analyse-ID eines bestehenden Objekts, das als Muster auf der Seite angezeigt wird.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Hero-Bereich</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Titel</Label>
            <Input value={form.hero_title} onChange={e => update('hero_title', e.target.value)} placeholder="Das WEG-Analysetool im Detail" data-testid="hero-title" />
          </div>
          <div>
            <Label className="text-xs">Untertitel</Label>
            <Input value={form.hero_subtitle} onChange={e => update('hero_subtitle', e.target.value)} placeholder="Transparente Bedarfsermittlung..." data-testid="hero-subtitle" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Inhalte</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">Über das Tool</Label>
            <textarea className="w-full border border-input rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring" rows={4} value={form.about_tool} onChange={e => update('about_tool', e.target.value)} placeholder="Was ist das WEG-Analysetool..." data-testid="about-tool" />
          </div>
          <div>
            <Label className="text-xs">So funktioniert die Analyse</Label>
            <textarea className="w-full border border-input rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring" rows={4} value={form.how_it_works} onChange={e => update('how_it_works', e.target.value)} placeholder="Die Analyse basiert auf drei Säulen..." data-testid="how-it-works" />
          </div>
          <div>
            <Label className="text-xs">Bedeutung für die Branche</Label>
            <textarea className="w-full border border-input rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring" rows={4} value={form.industry_value} onChange={e => update('industry_value', e.target.value)} placeholder="Die WEG-Verwaltungsbranche steht vor..." data-testid="industry-value" />
          </div>
          <div>
            <Label className="text-xs">Über uns (Scholze-Kurz & Kurz)</Label>
            <textarea className="w-full border border-input rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring" rows={4} value={form.about_company} onChange={e => update('about_company', e.target.value)} placeholder="Das WEG-Analysetool wird von der..." data-testid="about-company" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Funktionen-Überschrift</Label>
              <Input value={form.features_title} onChange={e => update('features_title', e.target.value)} placeholder="Funktionen im Überblick" data-testid="features-title" />
            </div>
            <div>
              <Label className="text-xs">CTA-Text (unten)</Label>
              <Input value={form.cta_text} onChange={e => update('cta_text', e.target.value)} placeholder="Überzeugen Sie sich selbst..." data-testid="cta-text" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
